import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/components/Seo";

export default function Login() {
  useSeo({ path: "/login", title: "Admin Login", description: "Hays Holdings admin login.", robots: "noindex, nofollow" });
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.access_token, data.user);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-charcoal px-6" data-testid="login-page">
      <div className="w-full max-w-md border border-white/10 bg-white p-8 md:p-10">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center bg-charcoal font-heading text-lg font-black text-white">HH</span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-charcoal">HAYS HOLDINGS INC</span>
        </div>
        <h1 className="mt-8 font-heading text-2xl font-bold text-charcoal">Admin Login</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to view incoming leads.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@pndoors.com" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          {error && <p data-testid="login-error" className="text-sm font-medium text-destructive">{error}</p>}
          <button type="submit" data-testid="login-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 bg-charcoal px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Lock size={16} />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
